import { Section } from '@advanza/advanza_generic'
import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { useToast } from 'components/misc/Toaster'
import { useCurrentCountry } from 'hooks/miscHooks'
import { hasAccess } from 'misc/user'
import { useEffect, useState } from 'react'

/**
 * Mirrors Api\Enum\ProspectValue. Keep in sync — the backend rejects any
 * prospect_value not in this enum, so configuring an unsupported value here
 * would only produce save errors.
 */
const PROSPECT_VALUES = [1, 2, 3, 5, 8, 0]

const PV_LABELS: Record<number, string> = {
    0: '€€€€*',
    1: '€',
    2: '€€',
    3: '€€€',
    5: '€€€€€',
    8: '€€€€€€€€',
}

interface ProspectValueConfig {
    promo_id?: number
    prospect_value: number
    is_active: boolean
    discount_percentage: number | null
    duration_months: number | null
    free_leads: number | null
    _isTouched?: boolean
}

interface SubscriptionDiscountOffer {
    'subscription-discount': {
        value: number
        type: string
        promo_length: number
    }
}

interface OneTimeLeadsOffer {
    'one-time-leads': {
        value: number
    }
}

type OfferInfo = SubscriptionDiscountOffer | OneTimeLeadsOffer

interface RawProspectValueConfig {
    promo_id?: number
    is_active?: boolean
    offers_info: OfferInfo[] | null
}

interface WinbackOfferConfig {
    reason_id: number
    reason_label: string
    is_active: boolean
    prospect_values: Record<number, ProspectValueConfig>
}

interface RawWinbackOfferConfig {
    reason_id: number
    reason_label: string
    is_active: boolean
    prospect_values: Record<number, RawProspectValueConfig>
}

type ConfigsState = Record<number, WinbackOfferConfig>

const parseOffersInfo = (
    offersInfo: OfferInfo[] | null,
): Omit<ProspectValueConfig, 'promo_id' | 'prospect_value' | 'is_active' | '_isTouched'> => {
    let discount_percentage: number | null = null
    let duration_months: number | null = null
    let free_leads: number | null = null

    for (const offer of offersInfo ?? []) {
        if ('subscription-discount' in offer) {
            discount_percentage = offer['subscription-discount'].value ?? null
            duration_months = offer['subscription-discount'].promo_length ?? null
        }
        if ('one-time-leads' in offer) {
            free_leads = offer['one-time-leads'].value ?? null
        }
    }

    return { discount_percentage, duration_months, free_leads }
}

const buildOffersInfo = (pv: ProspectValueConfig): OfferInfo[] => {
    const result: OfferInfo[] = []

    if (
        pv.discount_percentage !== null &&
        pv.discount_percentage > 0 &&
        pv.duration_months !== null &&
        pv.duration_months > 0
    ) {
        result.push({
            'subscription-discount': {
                value: pv.discount_percentage,
                type: 'percentage',
                promo_length: pv.duration_months,
            },
        })
    }

    if (pv.free_leads !== null && pv.free_leads > 0) {
        result.push({ 'one-time-leads': { value: pv.free_leads } })
    }

    return result
}

const defaultPvConfig = (prospectValue: number): ProspectValueConfig => ({
    prospect_value: prospectValue,
    is_active: false,
    discount_percentage: null,
    duration_months: null,
    free_leads: null,
})

const iconButtonStyle = (color: string): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    color,
    display: 'flex',
})

const PencilIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
)

const FloppyIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
    </svg>
)

const CloseIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
)

const TrashIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
)

const WinbackOfferSettingsList = () => {
    const [configs, setConfigs] = useState<ConfigsState>({})
    const [reasonOrder, setReasonOrder] = useState<number[]>([])
    const [loading, setLoading] = useState(true)
    const [savingPv, setSavingPv] = useState<string | null>(null)
    const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null)
    const [editingRows, setEditingRows] = useState<Set<string>>(new Set())
    const [editSnapshots, setEditSnapshots] = useState<Record<string, ProspectValueConfig>>({})
    const hasWriteAccess = hasAccess('winback_offers', 'w')
    const { addToast } = useToast()
    const country = useCurrentCountry()

    useEffect(() => {
        fetchConfigs()
    }, [country])

    const fetchConfigs = async () => {
        setLoading(true)
        try {
            const response = await call('office/winback-offer-settings/get-list')
            const raw: RawWinbackOfferConfig[] = response.configs ?? []

            const mapped: ConfigsState = {}
            for (const item of raw) {
                const pvMap: Record<number, ProspectValueConfig> = {}
                for (const pv of PROSPECT_VALUES) {
                    const existing = item.prospect_values[pv]
                    if (existing) {
                        pvMap[pv] = {
                            promo_id: existing.promo_id,
                            prospect_value: pv,
                            is_active: existing.is_active ?? false,
                            ...parseOffersInfo(existing.offers_info),
                        }
                    } else {
                        pvMap[pv] = defaultPvConfig(pv)
                    }
                }
                mapped[item.reason_id] = {
                    ...item,
                    prospect_values: pvMap,
                }
            }
            const order = raw.map((item) => item.reason_id)
            setConfigs(mapped)
            setReasonOrder(order)
            if (order.length > 0) {
                setSelectedReasonId((prev) => (prev !== null && mapped[prev] ? prev : order[0]))
            }
        } catch {
            addToast({ msg: 'Failed to load winback offer configurations.', red: true })
        } finally {
            setLoading(false)
        }
    }

    const updatePv = (
        reasonId: number,
        pv: number,
        field: keyof ProspectValueConfig,
        raw: string,
    ) => {
        setConfigs((prev) => {
            const reasonConfig = prev[reasonId]
            if (!reasonConfig) return prev

            const pvConfig = reasonConfig.prospect_values[pv]
            if (!pvConfig) return prev

            let value: boolean | number | null
            if (field === 'is_active') {
                value = raw === '1'
            } else if (raw === '') {
                value = null
            } else {
                const parsed = parseInt(raw, 10)
                if (Number.isNaN(parsed)) return prev

                let clamped = parsed
                if (field === 'discount_percentage') {
                    clamped = Math.max(0, Math.min(100, clamped))
                } else if (field === 'duration_months') {
                    clamped = Math.max(1, clamped)
                } else if (field === 'free_leads') {
                    clamped = Math.max(0, clamped)
                }
                value = clamped
            }

            return {
                ...prev,
                [reasonId]: {
                    ...reasonConfig,
                    prospect_values: {
                        ...reasonConfig.prospect_values,
                        [pv]: {
                            ...pvConfig,
                            [field]: value,
                            _isTouched: true,
                        },
                    },
                },
            }
        })
    }

    const enterEditMode = (reasonId: number, pv: number) => {
        const rowKey = `${reasonId}-${pv}`
        const pvConfig = configs[reasonId]?.prospect_values[pv]
        if (!pvConfig) return

        setEditSnapshots((prev) => ({ ...prev, [rowKey]: { ...pvConfig, _isTouched: false } }))
        setEditingRows((prev) => new Set(prev).add(rowKey))
    }

    const exitEditMode = (reasonId: number, pv: number) => {
        const rowKey = `${reasonId}-${pv}`
        setEditingRows((prev) => {
            const next = new Set(prev)
            next.delete(rowKey)
            return next
        })
        setEditSnapshots((prev) => {
            const next = { ...prev }
            delete next[rowKey]
            return next
        })
    }

    const cancelEditMode = (reasonId: number, pv: number) => {
        const rowKey = `${reasonId}-${pv}`
        const snapshot = editSnapshots[rowKey]
        const pvConfig = configs[reasonId]?.prospect_values[pv]

        if (snapshot && pvConfig?._isTouched) {
            setConfigs((prev) => {
                const reasonConfig = prev[reasonId]
                if (!reasonConfig) return prev
                return {
                    ...prev,
                    [reasonId]: {
                        ...reasonConfig,
                        prospect_values: {
                            ...reasonConfig.prospect_values,
                            [pv]: { ...snapshot, _isTouched: false },
                        },
                    },
                }
            })
        }

        exitEditMode(reasonId, pv)
    }

    const clearPvRow = (reasonId: number, pv: number) => {
        if (!window.confirm('Clear all values and deactivate this row?')) {
            return
        }
        setConfigs((prev) => ({
            ...prev,
            [reasonId]: {
                ...prev[reasonId],
                prospect_values: {
                    ...prev[reasonId].prospect_values,
                    [pv]: {
                        ...prev[reasonId].prospect_values[pv],
                        is_active: false,
                        discount_percentage: null,
                        duration_months: null,
                        free_leads: null,
                        _isTouched: true,
                    },
                },
            },
        }))
    }

    const savePvRow = async (reasonId: number, prospectValue: number) => {
        const pvConfig = configs[reasonId].prospect_values[prospectValue]
        if (!pvConfig._isTouched) return

        const key = `${reasonId}-${prospectValue}`
        setSavingPv(key)
        try {
            await call('office/winback-offer-settings/save', {
                method: 'POST',
                json: {
                    churn_reason_id: reasonId,
                    prospect_value: pvConfig.prospect_value,
                    is_active: pvConfig.is_active,
                    offers_info: buildOffersInfo(pvConfig),
                },
            })

            setConfigs((prev) => ({
                ...prev,
                [reasonId]: {
                    ...prev[reasonId],
                    prospect_values: {
                        ...prev[reasonId].prospect_values,
                        [prospectValue]: {
                            ...prev[reasonId].prospect_values[prospectValue],
                            _isTouched: false,
                        },
                    },
                },
            }))

            exitEditMode(reasonId, prospectValue)
            addToast({ msg: 'Saved successfully.', green: true })
        } catch {
            addToast({ msg: 'Failed to save. Please try again.', red: true })
        } finally {
            setSavingPv(null)
        }
    }

    if (loading) {
        return <div>Loading...</div>
    }

    const reasonList = reasonOrder.map((id) => configs[id]).filter(Boolean)
    const reason = selectedReasonId === null ? null : configs[selectedReasonId]

    return (
        <Section y>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                }}>
                <select
                    id="reason-select"
                    aria-label="Select churn reason"
                    value={selectedReasonId ?? ''}
                    onChange={(e) => setSelectedReasonId(Number(e.target.value))}
                    style={{
                        WebkitAppearance: 'menulist',
                        appearance: 'menulist',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px',
                        minWidth: '220px',
                        height: '36px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                    }}>
                    {reasonList.map((r) => (
                        <option key={r.reason_id} value={r.reason_id}>
                            {r.reason_label}
                        </option>
                    ))}
                </select>
            </div>

            {reason !== null && (
                <Section
                    y
                    style={{
                        border: '1px solid #ddd',
                        padding: '16px',
                        borderRadius: '4px',
                    }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd' }}>
                                <th style={{ padding: '8px', textAlign: 'left' }}>PV</th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>
                                    Subscription discount percentage
                                </th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>
                                    Duration (mo)
                                </th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>Free Leads</th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Active</th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {PROSPECT_VALUES.map((pv) => {
                                const pvConfig = reason.prospect_values[pv]
                                const rowActive = pvConfig.is_active
                                const rowKey = `${reason.reason_id}-${pv}`
                                const isEditing = editingRows.has(rowKey)
                                const inputsDisabled = !hasWriteAccess || !isEditing

                                return (
                                    <tr
                                        key={pv}
                                        style={{
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: rowActive ? '#e6f9e6' : 'transparent',
                                        }}>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>
                                            {PV_LABELS[pv] ?? `PV${pv}`}
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <InputField
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={pvConfig.discount_percentage ?? ''}
                                                disabled={inputsDisabled}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                ) =>
                                                    updatePv(
                                                        reason.reason_id,
                                                        pv,
                                                        'discount_percentage',
                                                        e.target.value,
                                                    )
                                                }
                                                style={{ width: '100px', margin: '0 auto' }}
                                            />
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <InputField
                                                type="number"
                                                min="1"
                                                value={pvConfig.duration_months ?? ''}
                                                disabled={inputsDisabled}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                ) =>
                                                    updatePv(
                                                        reason.reason_id,
                                                        pv,
                                                        'duration_months',
                                                        e.target.value,
                                                    )
                                                }
                                                style={{ width: '100px', margin: '0 auto' }}
                                            />
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <InputField
                                                type="number"
                                                min="0"
                                                value={pvConfig.free_leads ?? ''}
                                                disabled={inputsDisabled}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                ) =>
                                                    updatePv(
                                                        reason.reason_id,
                                                        pv,
                                                        'free_leads',
                                                        e.target.value,
                                                    )
                                                }
                                                style={{ width: '100px', margin: '0 auto' }}
                                            />
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={rowActive}
                                                aria-label={`Toggle active for PV ${pv}`}
                                                disabled={!hasWriteAccess}
                                                onClick={async () => {
                                                    const msg = rowActive
                                                        ? 'Deactivate this winback offer?'
                                                        : 'Activate this winback offer?'
                                                    if (!window.confirm(msg)) return
                                                    updatePv(
                                                        reason.reason_id,
                                                        pv,
                                                        'is_active',
                                                        rowActive ? '0' : '1',
                                                    )
                                                    const updatedPvConfig = {
                                                        ...pvConfig,
                                                        is_active: !rowActive,
                                                    }
                                                    const key = `${reason.reason_id}-${pv}`
                                                    setSavingPv(key)
                                                    try {
                                                        await call(
                                                            'office/winback-offer-settings/save',
                                                            {
                                                                method: 'POST',
                                                                json: {
                                                                    churn_reason_id:
                                                                        reason.reason_id,
                                                                    prospect_value:
                                                                        updatedPvConfig.prospect_value,
                                                                    is_active:
                                                                        updatedPvConfig.is_active,
                                                                    offers_info:
                                                                        buildOffersInfo(
                                                                            updatedPvConfig,
                                                                        ),
                                                                },
                                                            },
                                                        )
                                                        setConfigs((prev) => ({
                                                            ...prev,
                                                            [reason.reason_id]: {
                                                                ...prev[reason.reason_id],
                                                                prospect_values: {
                                                                    ...prev[reason.reason_id]
                                                                        .prospect_values,
                                                                    [pv]: {
                                                                        ...prev[reason.reason_id]
                                                                            .prospect_values[pv],
                                                                        _isTouched: false,
                                                                    },
                                                                },
                                                            },
                                                        }))
                                                        addToast({
                                                            msg: `Winback offer ${rowActive ? 'deactivated' : 'activated'}.`,
                                                            green: true,
                                                        })
                                                    } catch {
                                                        setConfigs((prev) => ({
                                                            ...prev,
                                                            [reason.reason_id]: {
                                                                ...prev[reason.reason_id],
                                                                prospect_values: {
                                                                    ...prev[reason.reason_id]
                                                                        .prospect_values,
                                                                    [pv]: {
                                                                        ...prev[reason.reason_id]
                                                                            .prospect_values[pv],
                                                                        is_active: rowActive,
                                                                        _isTouched: false,
                                                                    },
                                                                },
                                                            },
                                                        }))
                                                        addToast({
                                                            msg: 'Failed to update active state.',
                                                            red: true,
                                                        })
                                                    } finally {
                                                        setSavingPv(null)
                                                    }
                                                }}
                                                style={{
                                                    position: 'relative',
                                                    width: '40px',
                                                    height: '22px',
                                                    borderRadius: '11px',
                                                    border: 'none',
                                                    backgroundColor: rowActive
                                                        ? '#2e7d32'
                                                        : '#bdbdbd',
                                                    cursor: hasWriteAccess ? 'pointer' : 'default',
                                                    transition: 'background-color 0.2s',
                                                    padding: 0,
                                                }}>
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: rowActive ? '20px' : '2px',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#fff',
                                                        transition: 'left 0.2s',
                                                    }}
                                                />
                                            </button>
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '6px',
                                                    alignItems: 'center',
                                                }}>
                                                {hasWriteAccess && !isEditing && (
                                                    <button
                                                        type="button"
                                                        title="Edit row"
                                                        aria-label={`Edit PV ${pv}`}
                                                        onClick={() =>
                                                            enterEditMode(reason.reason_id, pv)
                                                        }
                                                        style={iconButtonStyle('#616161')}>
                                                        <PencilIcon />
                                                    </button>
                                                )}
                                                {hasWriteAccess &&
                                                    isEditing &&
                                                    pvConfig._isTouched && (
                                                        <button
                                                            type="button"
                                                            title="Save row"
                                                            aria-label={`Save PV ${pv}`}
                                                            disabled={savingPv === rowKey}
                                                            onClick={() =>
                                                                savePvRow(reason.reason_id, pv)
                                                            }
                                                            style={iconButtonStyle('#1976d2')}>
                                                            <FloppyIcon />
                                                        </button>
                                                    )}
                                                {hasWriteAccess && isEditing && (
                                                    <button
                                                        type="button"
                                                        title="Exit edit mode"
                                                        aria-label={`Exit edit mode for PV ${pv}`}
                                                        onClick={() =>
                                                            cancelEditMode(reason.reason_id, pv)
                                                        }
                                                        style={iconButtonStyle('#616161')}>
                                                        <CloseIcon />
                                                    </button>
                                                )}
                                                {hasWriteAccess && (
                                                    <button
                                                        type="button"
                                                        title="Clear row"
                                                        aria-label={`Clear PV ${pv}`}
                                                        onClick={() =>
                                                            clearPvRow(reason.reason_id, pv)
                                                        }
                                                        style={iconButtonStyle('#d32f2f')}>
                                                        <TrashIcon />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </Section>
            )}
        </Section>
    )
}

export default WinbackOfferSettingsList
